import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ManageRequestsService } from 'src/app/core/components/manage-requests/manage-requests.service';
import { UtilService } from '../helper/util.service';

// Generic interfaces for reusable dialog - Export for use in other components
export interface SelectionGroup {
  code: string;
  name: string;
  options: string[];
  selectedOptions: string[];
}

export interface DependentSelection {
  parentKey: string; // e.g., "AGB-TC"
  parentLabel: string; // e.g., "AGB - TC"
  options: string[];
  selectedOptions: string[];
}

export interface DependentMapping {
  [key: string]: string[]; 
}

export interface DialogConfig {
  title: string;
  step1Label: string; 
  step2Label?: string; 
  selectionGroups: SelectionGroup[]; 
  dependentMapping?: DependentMapping; 
  multiStep?: boolean;
  validationMessage?: string;
  module?: any; 
  stepDetails?:any;
  metadata?: {
    [key: string]: string; 
  };
}

@Component({
    selector: 'app-request-access-dialog',
    templateUrl: './request-access-dialog.component.html',
    styleUrl: './request-access-dialog.component.scss',
    standalone: false
})
export class RequestAccessDialogComponent implements OnInit {
  currentStep: number = 1;
  totalSteps: number = 1;
  validationError: string = '';
  
  // Generic data structures
  selectionGroups: SelectionGroup[] = [];
  dependentSelections: DependentSelection[] = [];
  dependentMapping: DependentMapping = {};
  
  // Display labels
  dialogTitle: string = '';
  step1Label: string = '';
  step2Label: string = '';
  validationMessage: string = '';
  metadata: { [key: string]: string } = {};
  moduleDetails: any;
  dropdownStates: boolean[] = []; // Track dropdown state for each index
  activeHeaderName: any;
  stepDetails: any;
  stepOneDetails: any;
  bussinessProductLine: any;
  userName: string;
  companyName: any;
  companyId: any;
  currentStepselected = 0
  // Preserve selections per step index
  private savedSelectionsByStep: { [stepIndex: number]: { [groupCode: string]: string[] } } = {};
  constructor(
    public dialogRef: MatDialogRef<RequestAccessDialogComponent>,
    private manageRequestService: ManageRequestsService,
    @Inject(MAT_DIALOG_DATA) public data: DialogConfig,
     private utilService: UtilService,
  ) {}

  ngOnInit(): void {
    // Initialize from config
    this.moduleDetails = this.data.module;
    this.stepDetails = this.data.stepDetails;
    this.dialogTitle = this.data.title || 'Request Access';
    this.getDataByEndPoint(this.stepDetails[this.currentStepselected],this.moduleDetails);
    this.setPopupDetails();
  }
  setPopupDetails(){
    this.metadata = this.userDetails();
    this.totalSteps = this.stepDetails?.length;
  }
  userDetails(){
    this.userName = localStorage.getItem('userName');
    this.utilService.companyName.subscribe((res:any) => this.companyName = res);
    this.utilService.companyId.subscribe((res:any) => this.companyId = res);
    let metadata= {
          'Requested by': this.userName || 'Unknown User',
          'Organisation': this.companyName || 'Unknown Org',
          'Module': this.moduleDetails.moduleName
        }
        return metadata || {};
  }
  toggleOption(selectionGroup: SelectionGroup, option: string): void {
    const index = selectionGroup.selectedOptions.indexOf(option);
    if (index > -1) {
      selectionGroup.selectedOptions.splice(index, 1);
    } else {
      selectionGroup.selectedOptions.push(option);
    }
    // Keep saved state in sync for the current step
    this.saveCurrentStepSelections();
  }
  onDropdownMenuVisible(index: number) {
    // Initialize dropdownStates array if needed
    if (!this.dropdownStates[index]) {
      this.dropdownStates[index] = false;
    }
    // Toggle the specific dropdown at the given index
    this.dropdownStates[index] = !this.dropdownStates[index];
    
    //Optional: Close other dropdowns when opening a new one
    this.dropdownStates.forEach((state, i) => {
      if (i !== index) {
        this.dropdownStates[i] = false;
      }
    });
  }

   isOptionSelected(selectionGroup: SelectionGroup, option: string): boolean {
    return selectionGroup.selectedOptions.includes(option);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onNext(stepNumber:any,index:any): void {
    if(!this.hasSelections()){
      this.getValidationError();
      return 
    }
    if (this.totalSteps === 1) {
      // Single step mode - close with data
      const selectedData = this.selectionGroups
        .filter(sg => sg.selectedOptions.length > 0)
        .map(sg => ({
          groupCode: sg.code,
          groupName: sg.name,
          selectedOptions: sg.selectedOptions
        }));
      
      this.dialogRef.close(selectedData);
    } else {
      // Multi-step mode - go to step 2
      this.currentStep = 2;
      
      this.currentStepselected = stepNumber;
      
      this.callNextStepSelections(this.moduleDetails)
      this.validationError = '';
    }
  }

  onBack(stepNumber:any): void {
    this.validationError = ''
    this.currentStepselected = stepNumber - 2;
    // Restore previous selections for the target step after data loads
    this.getDataByEndPoint(this.stepDetails[this.currentStepselected],this.moduleDetails, null, true)
  }

  getValidationError(){
    const isValid = this.hasSelections();
    
    if (!isValid) {
      this.validationMessage = this.stepDetails[this.currentStepselected].subStepWarning;;
      this.validationError = this.validationMessage;
    }
  }
  onSubmit(): void {
    let isValidationError = this.stepDetails[this.currentStepselected].subStepWarning;
    if(isValidationError){
      const isValid = this.validateDependentSelections();
      if (!isValid) {
        this.validationMessage =this.stepDetails[this.currentStepselected].subStepWarning;
        this.validationError = this.validationMessage;
        return;
      }
    }
    // Construct payload in the required format with userGroups
    const userGroups: any[] = [];
    
    this.selectionGroups.forEach(ds => {
      // Add selected options with isAssigned: true
      ds.selectedOptions.forEach(selectedOption => {
        if(ds.code){
          userGroups.push({
            userGroupName: `${ds.code}-${selectedOption}`,
            isAssigned: true
          });
       }else{
          userGroups.push({
            userGroupName: `${selectedOption}`,
            isAssigned: true
          });
       }
      });
    });

    const payload = {
      isModuleAttached: true,
      userGroups: userGroups
    };
   
    this.dialogRef.close(payload);
   
  }

  mapSelectionGroupsToPayload(): { [key: string]: string[] } {
    const payload: { [key: string]: string[] } = {};
    
    this.selectionGroups.forEach(group => {
      if (group.selectedOptions.length > 0) {
        payload[group.code] = group.selectedOptions;
      }
    });
    
    return payload;
  }
  toggleDependentOption(dependentSelection: DependentSelection, option: string): void {
    const index = dependentSelection.selectedOptions.indexOf(option);
    if (index > -1) {
      dependentSelection.selectedOptions.splice(index, 1);
    } else {
      dependentSelection.selectedOptions.push(option);
    }
  }

  isDependentOptionSelected(dependentSelection: DependentSelection, option: string): boolean {
    return dependentSelection.selectedOptions.includes(option);
  }

  validateDependentSelections(): boolean {
    return  this.selectionGroups.every(ds => ds.selectedOptions.length > 0);
  }
  
  clearAllSelectionsRegions(){
    this.dependentSelections.forEach(ds => {
      ds.selectedOptions = [];
    });

    this.validationError = '';
  }
  hasSelections(): boolean {
    return this.selectionGroups.some(sg => sg.selectedOptions.length > 0);
  }
  clearAllSelections(){
    this.selectionGroups.forEach(sg => {
      sg.selectedOptions = [];
    });
    this.validationError = '';
  }

  selectAllGroups(selectionGroup:any){
  selectionGroup.selectedOptions = [...selectionGroup.options];
  }
  unselectAllGroups(selectionGroup:any){
  selectionGroup.selectedOptions = [];
  }
  areAllOptionsSelected(selectionGroup:any): boolean {
    return selectionGroup.selectedOptions.length === selectionGroup.options.length;
  }
  deleteSelectionGroup(selectionGroup:any){
    const index = this.selectionGroups.indexOf(selectionGroup);
    if (index > -1) {
      this.selectionGroups.splice(index, 1);
    }
  }
//region
  selectAllRegions(selectionGroup:any){
  selectionGroup.selectedOptions = [...selectionGroup.options];
  }
  unselectAllRegions(selectionGroup:any){
  selectionGroup.selectedOptions = [];
  }
  areAllRegionsSelected(selectionGroup:any): boolean {
    return selectionGroup.selectedOptions.length === selectionGroup.options.length;
  }
  deleteSelectionRegions(selectionGroup:any){
    const index = this.dependentSelections.indexOf(selectionGroup);
    if (index > -1) {
      this.dependentSelections.splice(index, 1);
    }
  }
  canSubmit(): boolean {
    return this.validateDependentSelections();
  }

  // Helper method for template
  getMetadataKeys(): string[] {
    return Object.keys(this.metadata);
  }

  hasMetadata(): boolean {
    return Object.keys(this.metadata).length > 0;
  }

getStepOneDetails(module: any){
    this.manageRequestService.getDataForSteps(module.moduleId).subscribe((res: any) => {
          if(res){
            this.stepOneDetails = res.result;
            this.getDataByEndPoint(this.stepOneDetails[this.currentStepselected], module);
        }
    });
  }

  getDataByEndPoint(stepOneDetails: any,module:any,payload:any=null, restoreSelections:boolean=false){
    this.manageRequestService.getDataByEndpoint(module.moduleId,stepOneDetails.stepApiEndpoint,payload).subscribe((res: any) => {
          if(res){
            this.bussinessProductLine = res.result;
            const businessLinesConfig = this.mapBusinessLinesConfig(res.result);
            // Restore selections only when requested (e.g., on Back)
            this.selectionGroups = restoreSelections
              ? this.restoreSelectionsForStep(this.currentStepselected, businessLinesConfig)
              : businessLinesConfig;
          }
        });
  }
 mapBusinessLinesConfig(result: any) {
    const businessLineNames: { [key: string]: string } = {};
    
    // Handle array input - convert to single selection group with all items as options
    if (Array.isArray(result)) {
      return [{
        code: '',
        name: 'Available Options',
        options: result,
        selectedOptions: [] as string[]
      }];
    }
    
    // Handle object input with key-value pairs (existing logic)
    const businessLinesConfig: any[] = Object.keys(result).map(code => ({
      code: code,
      name: businessLineNames[code] || code,
      options: result[code as keyof typeof result],
      selectedOptions: [] as string[]
    }));
    
    return businessLinesConfig || [];
  }

  callNextStepSelections(module: any): void {
    this.dependentSelections = [];
    if(this.currentStepselected == 1){
      const payload = this.mapSelectionGroupsToPayload();
      this.getDataByEndPoint(this.stepDetails[this.currentStepselected],module,payload)
    } 
  }

  // --- Selection persistence helpers ---
  private saveCurrentStepSelections(): void {
    const selections: { [groupCode: string]: string[] } = {};
    this.selectionGroups.forEach(group => {
      selections[group.code] = [...group.selectedOptions];
    });
    this.savedSelectionsByStep[this.currentStepselected] = selections;
  }

  private restoreSelectionsForStep(stepIndex: number, groups: SelectionGroup[]): SelectionGroup[] {
    const saved = this.savedSelectionsByStep[stepIndex];
    if (!saved) {
      return groups;
    }
    return groups.map(g => ({
      ...g,
      selectedOptions: Array.isArray(saved[g.code]) ? [...saved[g.code]] : []
    }));
  }

  
}
